import { Menu } from '#app/components/Menu/Menu.js';
import { Tooltip } from '#app/components/Tooltip/Tooltip.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { useIsMobile } from '#app/hooks/useIsMobile.js';
import { AddToListMenuSelect } from './AddToListMenuSelect.js';
import { type AddToListMenuReturn } from './useAddToListMenu.js';
import { ChevronRightIcon } from '@contra/icons/icons';
import { ReactNode } from 'react';
import { styled } from 'styled-components';

type Item = {
  id: string;
  isExpertList: boolean;
  name: string;
};

const Container = styled.div`
  display: flex;
  align-items: center;
  width: fit-content;
  text-align: left;
`;

const StyledArrowContainer = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;

  > svg {
    color: ${({ theme }) => theme.colorsV2.gray70};
    flex-shrink: 0;
  }
`;

const StyledMenuItemContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

type AddToListMenuPrimitiveProps = {
  readonly addToListMenuProps: AddToListMenuReturn;
  readonly analyticsSection: string;
  readonly availableLists: Item[];
  readonly isModalOpen: boolean;
  readonly isSaved: boolean;
  readonly onClose?: () => void;
  readonly onListDeleted: (listId: string) => void;
  readonly onListNameUpdated: (listId: string, newName: string) => void;
  readonly onNewListCreated: (newListName: string) => void;
  readonly onSelectedItemChange: (newLists: Item[]) => void;
  readonly onUnsave: () => void;
  readonly selectedLists: Item[];
  readonly trigger: ReactNode;
};

export const AddToListMenuPrimitive = ({
  addToListMenuProps,
  analyticsSection,
  availableLists,
  isModalOpen,
  isSaved,
  onClose,
  onListDeleted,
  onListNameUpdated,
  onNewListCreated,
  onSelectedItemChange,
  onUnsave,
  selectedLists,
  trigger,
}: AddToListMenuPrimitiveProps) => {
  const { track } = useAnalytics();
  const isMobile = useIsMobile();

  const {
    floatingProps,
    handleCloseListMenu,
    handleOpenListMenu,
    isActionsMenuOpen,
    isListMenuOpen,
    setIsActionsMenuOpen,
    toggleActionsMenuOpen,
  } = addToListMenuProps;

  const floatingRef = floatingProps.reference;

  const button = <div ref={isSaved ? undefined : floatingRef}>{trigger}</div>;

  const buttonWithMaybeTooltip = isSaved ? (
    button
  ) : (
    <Tooltip
      content="Save"
      width="auto"
    >
      {button}
    </Tooltip>
  );

  const menu = (
    <AddToListMenuSelect
      analyticsSection={analyticsSection}
      availableLists={availableLists}
      isOpen={isListMenuOpen}
      isParentMenuOpen={isModalOpen}
      onClose={() => {
        onClose?.();

        handleCloseListMenu();
      }}
      onListDeleted={onListDeleted}
      onListNameUpdated={onListNameUpdated}
      onNewListCreated={onNewListCreated}
      onSelectedItemChange={onSelectedItemChange}
      selectedLists={selectedLists}
      {...floatingProps}
    />
  );

  if (isSaved) {
    return (
      <Container>
        <Menu
          isOpen={isActionsMenuOpen}
          offset={[0, 0]}
          placement="bottom"
          strategy="absolute"
          toggleMenu={toggleActionsMenuOpen}
        >
          <Menu.Reference>{buttonWithMaybeTooltip}</Menu.Reference>
          <Menu.Panel
            allowPanelOverflow
            aria-label="Add to list menu actions"
            disableCloseOnOutsideClick={isListMenuOpen || isModalOpen}
          >
            <Menu.Item
              aria-label="Add to list"
              elementRef={isSaved ? floatingRef : undefined}
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
                handleOpenListMenu();
                setIsActionsMenuOpen(true);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  handleOpenListMenu();
                  setIsActionsMenuOpen(true);
                }
              }}
              onMouseEnter={() => {
                handleOpenListMenu();
              }}
            >
              <StyledMenuItemContent>
                Add to list
                {isMobile ? null : (
                  <StyledArrowContainer>
                    <ChevronRightIcon size={16} />
                  </StyledArrowContainer>
                )}
              </StyledMenuItemContent>
            </Menu.Item>
            <Menu.Item
              aria-label="Unsave"
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
                setIsActionsMenuOpen(false);
                onUnsave();

                track('button', {
                  action: 'tapped',
                  button_name: 'remove_contact',
                  section: analyticsSection,
                });
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  setIsActionsMenuOpen(false);
                  onUnsave();

                  track('button', {
                    action: 'tapped',
                    button_name: 'remove_contact',
                    section: analyticsSection,
                  });
                }
              }}
            >
              Unsave
            </Menu.Item>
          </Menu.Panel>
        </Menu>
        {menu}
      </Container>
    );
  }

  return (
    <Container>
      {buttonWithMaybeTooltip}
      {menu}
    </Container>
  );
};
